import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/da2l9i2amq5jpe1hrvm0';

const contacts = [
  { icon: 'ri-mail-line', label: '邮箱', value: 'hello@waiwai-manhua.example' },
  { icon: 'ri-wechat-line', label: '公众号', value: '歪歪漫画' },
  { icon: 'ri-time-line', label: '回复时间', value: '工作日 1-2 天内回复' },
];

export default function ContactPage() {
  const [formError, setFormError] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    document.title = '联系我们 · 意见反馈与商务合作 | 韩国歪歪漫画';
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    setFormError('');
    setStatus('submitting');

    const honeypot = formData.get('company_alt')?.toString().trim() ?? '';
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('company_alt');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后重试');
        setStatus('error');
      }
    } catch {
      setFormError('网络异常，请稍后再试');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <section className="bg-background-100 pt-24 pb-8 md:pt-32 md:pb-10">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <nav className="text-xs text-foreground-600" aria-label="面包屑">
            <ol className="flex flex-wrap items-center gap-1">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-900">联系我们</li>
            </ol>
          </nav>
          <h1 className="mt-4 font-heading text-4xl text-foreground-950 md:text-5xl">联系我们</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
            无论是想推荐漫画题材、反馈阅读问题，还是有合作想法，都欢迎给我们留言。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.3fr]">
          <div>
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">联系方式</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              你可以在任意时间留言，我们会认真查看每一条建议。你的反馈会直接帮助我们把内容做得更好。
            </p>
            <div className="mt-6 space-y-3">
              {contacts.map((c) => (
                <div key={c.label} className="flex items-center gap-4 rounded-2xl border border-background-200 bg-background-50 p-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                    <i className={`${c.icon} text-xl`}></i>
                  </span>
                  <div>
                    <div className="text-xs text-foreground-600">{c.label}</div>
                    <div className="mt-0.5 font-medium text-foreground-950">{c.value}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-2xl bg-accent-100 p-6">
              <div className="flex items-center gap-3">
                <i className="ri-heart-3-fill text-2xl text-accent-600"></i>
                <p className="text-sm leading-relaxed text-accent-900">感谢每一个喜欢歪歪漫画的你，你们的支持是我们持续更新的动力。</p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-2xl text-foreground-950">给我们留言</h2>
            <form data-readdy-form="da2l9i2amq5jpe1hrvm0" onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-2 block text-sm font-medium text-foreground-800">你的称呼</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="w-full rounded-xl border border-background-300 bg-background-100 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground-800">邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full rounded-xl border border-background-300 bg-background-100 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="topic" className="mb-2 block text-sm font-medium text-foreground-800">留言类型</label>
                <select
                  id="topic"
                  name="topic"
                  className="w-full rounded-xl border border-background-300 bg-background-100 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400"
                >
                  <option value="建议">内容建议</option>
                  <option value="反馈">问题反馈</option>
                  <option value="合作">合作意向</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-2 block text-sm font-medium text-foreground-800">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="想和我们说什么都可以，500 字以内"
                  className="w-full rounded-xl border border-background-300 bg-background-100 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400"
                ></textarea>
                <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
              </div>

              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-honeypot"
              />

              {formError && (
                <div className="rounded-xl bg-primary-50 border border-primary-200 px-4 py-3 text-sm text-primary-700">{formError}</div>
              )}
              {status === 'success' && (
                <div className="rounded-xl bg-accent-100 border border-accent-200 px-4 py-3 text-sm text-accent-900">
                  提交成功！我们会尽快回复你，感谢你的留言。
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap sm:w-auto"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> 提交中
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i> 提交留言
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}